import { DateTime } from 'luxon';
import { DB_FIELD_LABELS } from './importFields';
import { getImportRowNumber, normalizeImportValue, parseImportDate } from './importValues';
import { normalizeRfidUid } from './rfid';

export const IMPORT_VALUE_CHOICES = {
  inspection_result: ['Pass', 'Fail'],
  inspection_type: ['Visual', 'Close', 'Detailed'],
};
export const IMPORT_DATE_FORMATS = [
  { value: 'auto', label: 'Recognize unambiguous dates automatically' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD (2026-10-23)' },
  { value: 'd/M/yyyy', label: 'Day/month/year (23/10/2026)' },
  { value: 'M/d/yyyy', label: 'Month/day/year (10/23/2026)' },
  { value: 'd.M.yyyy', label: 'Day.month.year (23.10.2026)' },
  { value: 'd-MMM-yy', label: 'Day-month-year (23-Oct-26)' },
  { value: 'd-MMM-yyyy', label: 'Day-month-year (23-Oct-2026)' },
];
const OPTIONAL_IDENTIFIERS = new Set(['model_no', 'serial_no', 'certificate_number', 'work_order']);
const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const isCellError = value => value && typeof value === 'object' && typeof value.__importCellError === 'string';
const displayValue = value => isCellError(value) ? value.originalText || value.__importCellError
  : value instanceof Date ? (Number.isFinite(value.getTime()) ? value.toISOString() : 'Invalid date')
    : value ?? '';
const isBlank = value => value === null || value === undefined || String(value).trim() === '';

const HEADER_ALIASES = {
  equipment_tag: ['Tag No.', 'Tag Number', 'Tag', 'Equipment Tag'],
  old_rfid: ['RFID Tag No.', 'RFID Tag', 'RFID', 'RFID UID', 'Existing RFID tag ID'],
  inspection_date: ['Last Inspection Date', 'Inspection Date'],
  inspection_result: ['Last Inspection Result', 'Inspection Result'],
  inspection_type: ['Last Inspection Grade', 'Inspection Grade', 'Inspection Type'],
  inspector: ['Last Inspected By', 'Inspected By', 'Inspector'],
  area: ['Module/Location No.', 'Area'],
  description: ['Equipment Description', 'Description'],
  zone: ['Hazardous Area Classification', 'Zone'],
  category_epl: ['Category / EPL'],
  system_component: ['System / Component', 'System Component'],
  manufacturer: ['Manufacturer'],
  model_no: ['Model No./Type', 'Model No.', 'Model Number'],
  serial_no: ['Serial No.', 'Serial Number'],
  protection_class: ['Type of Protection', 'Protection Class'],
  gas_group: ['Gas Group'],
  temperature_class: ['T Class (T1-T6)', 'Temperature Class', 'T Class'],
  ip_rating: ['IP Rating'],
  certifying_authority: ['Certifying Authority'],
  certificate_number: ['Certificate No.', 'Certificate Number'],
  work_order: ['Work Order', 'Work Order No.'],
};
const headerKey = header => String(header).trim().toLowerCase().replace(/[^a-z0-9]/g, '');

// Only suggest an unambiguous match. In particular ATEX Category and EPL remain
// separate source columns until the user chooses how to map them.
export const suggestImportMapping = (headers, availableFields) => {
  const mapping = {};
  availableFields.forEach(field => {
    const exact = headers.filter(header => header === field);
    if (exact.length === 1) {
      mapping[field] = exact[0];
      return;
    }
    const names = new Set([field, DB_FIELD_LABELS[field], ...(HEADER_ALIASES[field] || [])].map(headerKey));
    const candidates = headers.filter(header => names.has(headerKey(header)));
    if (candidates.length === 1) mapping[field] = candidates[0];
  });
  return mapping;
};

const allowedActions = (field, rawValue) => [
  'replace',
  ...(field === 'equipment_tag' ? [] : ['blank']),
  ...(OPTIONAL_IDENTIFIERS.has(field) && typeof rawValue === 'string' ? ['keep'] : []),
];
const valueIssue = (field, code, message, rawValue) => ({
  field, code, message, rawValue: displayValue(rawValue), allowedActions: allowedActions(field, rawValue),
});
const errorCode = (field, value, message) => {
  if (field === 'inspection_date') return 'invalid_date';
  if (field === 'old_rfid') return 'invalid_rfid';
  if (value instanceof Date) return 'date_in_text';
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) >= 1e15)) return 'numeric_precision';
  if (/scientific notation/i.test(message)) return 'scientific_notation';
  if (/apostrophe/i.test(message)) return 'leading_apostrophe';
  return 'invalid_value';
};

const selectedDate = (value, format) => {
  if (isBlank(value)) return null;
  if (value instanceof Date || !format || format === 'auto') return parseImportDate(value);
  if (!IMPORT_DATE_FORMATS.some(option => option.value === format)) throw new Error('Choose a supported source date format.');
  const date = DateTime.fromFormat(String(value).trim(), format, { zone: 'utc', locale: 'en' });
  if (!date.isValid || date.year < 1900 || date.year > 9999) {
    throw new Error(`Date does not match the selected source format (${format}).`);
  }
  return date.toISODate();
};

export const buildImportReview = (rows, mapping, availableFields, options = {}) => {
  const fields = Object.entries(mapping).filter(([field, column]) => availableFields.includes(field) && column);
  const headers = [...new Set(rows.flatMap(row => Object.keys(row)))];
  const mappedColumns = new Set(fields.map(([, column]) => column));
  const unmappedColumns = headers.filter(header => !mappedColumns.has(header));
  const mappingIssues = [];
  if (!fields.some(([field]) => field === 'equipment_tag')) mappingIssues.push('Map the mandatory Equipment tag column before importing.');
  fields.forEach(([field, column]) => {
    if (!headers.includes(column)) mappingIssues.push(`The source column mapped to ${DB_FIELD_LABELS[field]} is missing: ${column}.`);
  });
  const firstRowByTag = new Map();
  const entriesByRfid = new Map();
  const dateCache = new Map();
  const parseDate = value => {
    const cacheKey = `${value instanceof Date ? 'date' : typeof value}:${displayValue(value)}`;
    if (!dateCache.has(cacheKey)) {
      try { dateCache.set(cacheKey, { value: selectedDate(value, options.dateFormat || 'auto') }); }
      catch (error) { dateCache.set(cacheKey, { error }); }
    }
    const result = dateCache.get(cacheKey);
    if (result.error) throw result.error;
    return result.value;
  };
  const normalize = (field, value, formatOverride) => {
    if (isCellError(value)) {
      const error = new Error(value.__importCellError);
      error.code = 'invalid_cell';
      throw error;
    }
    if (field === 'old_rfid') return normalizeRfidUid(value);
    if (field === 'inspection_date') return formatOverride ? selectedDate(value, formatOverride) : parseDate(value);
    const normalized = normalizeImportValue(field, value);
    if (IMPORT_VALUE_CHOICES[field] && normalized) {
      const canonical = IMPORT_VALUE_CHOICES[field].find(choice => choice.toLowerCase() === normalized.toLowerCase());
      if (!canonical) throw new Error(`Choose how to convert "${normalized}" to ${IMPORT_VALUE_CHOICES[field].join(' or ')}, or explicitly leave this field blank.`);
      return canonical;
    }
    return normalized;
  };

  const entries = rows.map((row, index) => {
    const rowNumber = getImportRowNumber(row, index);
    const entry = { index, rowNumber, tag: '', status: 'ready', values: {}, issues: [], decisions: [], source: row };
    if (mappingIssues.length) {
      entry.status = 'review';
      entry.issues = mappingIssues.map(message => valueIssue('equipment_tag', 'missing_mapping', message, ''));
      return entry;
    }
    const fieldValue = (field, rawValue) => {
      const resolution = options.resolutions?.[rowNumber]?.[field];
      let value = rawValue;
      let action = null;
      let formatOverride = null;
      if (resolution) {
        action = resolution.action;
        if (!allowedActions(field, rawValue).includes(action)) {
          entry.issues.push(valueIssue(field, 'invalid_resolution', 'This resolution is not supported for this field. Replace the value with a verified value.', rawValue));
          return null;
        }
        if (action === 'blank') value = null;
        if (action === 'replace') {
          value = resolution.value;
          // A per-cell correction uses an unambiguous date, independent of the
          // source file's selected format. Original source dates retain it.
          if (field === 'inspection_date') formatOverride = 'auto';
        }
        if (action === 'keep') {
          entry.decisions.push({ field, action, originalValue: displayValue(rawValue), value: rawValue });
          return rawValue;
        }
      } else if (IMPORT_VALUE_CHOICES[field] && !isCellError(rawValue) && !isBlank(rawValue)) {
        const lookup = String(rawValue).trim();
        const choices = options.valueMappings?.[field];
        if (own(choices, lookup)) {
          value = choices[lookup];
          action = 'map';
        }
      }
      try {
        const normalized = normalize(field, value, formatOverride);
        if (action) entry.decisions.push({ field, action, originalValue: displayValue(rawValue), value: normalized });
        return normalized;
      } catch (error) {
        entry.issues.push(valueIssue(field, error.code || (IMPORT_VALUE_CHOICES[field] ? 'unknown_value' : errorCode(field, value, error.message)), error.message, rawValue));
        return null;
      }
    };

    const rawTag = row[mapping.equipment_tag];
    entry.tag = displayValue(rawTag);
    entry.values.equipment_tag = fieldValue('equipment_tag', rawTag);
    if (!entry.issues.length && !entry.values.equipment_tag) {
      entry.status = 'skipped';
      entry.issues.push(valueIssue('equipment_tag', 'missing_tag', 'Missing mandatory equipment tag. This item will not be imported.', rawTag));
      return entry;
    }
    if (!entry.issues.length) {
      entry.tag = entry.values.equipment_tag;
      if (firstRowByTag.has(entry.tag)) {
        entry.status = 'skipped';
        entry.issues.push(valueIssue('equipment_tag', 'duplicate_tag', `Duplicate equipment tag "${entry.tag}" (first appears in row ${firstRowByTag.get(entry.tag)}). This item will not be imported.`, rawTag));
        return entry;
      }
      firstRowByTag.set(entry.tag, rowNumber);
    }
    fields.forEach(([field, column]) => {
      if (field !== 'equipment_tag') entry.values[field] = fieldValue(field, row[column]);
    });
    if (entry.values.old_rfid) {
      const matching = entriesByRfid.get(entry.values.old_rfid) || [];
      matching.push(entry);
      entriesByRfid.set(entry.values.old_rfid, matching);
    }
    entry.status = entry.issues.length ? 'review' : 'ready';
    return entry;
  });

  entriesByRfid.forEach((matching, uid) => {
    if (matching.length < 2) return;
    const rowNumbers = matching.map(entry => entry.rowNumber).join(', ');
    matching.forEach(entry => {
      entry.status = 'review';
      entry.issues.push(valueIssue('old_rfid', 'duplicate_rfid', `RFID ${uid} is assigned to multiple items in rows ${rowNumbers}. Verify the correct association or explicitly leave the RFID blank.`, entry.source[mapping.old_rfid]));
    });
  });
  const counts = entries.reduce((summary, entry) => {
    summary[entry.status] += 1;
    return summary;
  }, { total: entries.length, ready: 0, review: 0, skipped: 0 });
  return { entries, counts, unmappedColumns, mappingIssues };
};
